class SwiperContainer extends HTMLElement {
    defaultOptions;

    constructor() {
        super();

        this.defaultOptions = {
            loop: false,
            autoplay: false,
            spaceBetween: '0',
            navigation: false,
            slidesPerView: 1,
            pagination: false,
            centeredSlides: false,
            centerInsufficientSlides: false,
            direction: 'horizontal',
            loopedSlides: 1,
            loopAdditionalSlides: 0,
            slideToClickedSlide: false,
            thumbs: false,
            watchSlidesProgress: false,
            breakpoints: false,
            slidesPerGroup: 1,
            delay: 2000,
            initialSlide: 0,
            nested: false,
            observer: true,
            observeParents: true,
            effect: 'slide',
            creativeEffect: null,
            speed: 300,
            grabCursor: false,
        };
    }

    connectedCallback() {
        // Görünür olduğunda başlat (IntersectionObserver ile lazy init)
        if ('IntersectionObserver' in window) {
            this._observer = new IntersectionObserver(
                (entries, observer) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            observer.unobserve(entry.target);
                            this._observer = null;
                            this.init();
                        }
                    });
                },
                { rootMargin: '200px' },
            );
            this._observer.observe(this);
        } else {
            // Fallback: DOMContentLoaded kullan (load yerine)
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
            } else {
                this.init();
            }
        }
    }

    disconnectedCallback() {
        if (this._observer) {
            this._observer.unobserve(this);
            this._observer = null;
        }
        if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
        }
    }

    init = () => {
        const el = this.querySelector('.swiper');
        if (!el) return;

        const options = this.getOptions();

        // Extract manual navigation info before passing to Swiper
        const manualNav = options._manualNavigation;
        delete options._manualNavigation;

        // Nested Swiper'larda observer devre dışı bırak (performans)
        if (this.getAttribute('nested') === 'true') {
            options.observer = false;
            options.observeParents = false;
        }

        this.swiper = new Swiper(el, options);

        // Manually attach navigation button handlers for better reliability
        if (manualNav) {
            const nextBtn = manualNav.nextEl;
            const prevBtn = manualNav.prevEl;

            if (nextBtn) {
                // Remove disabled attribute to ensure button is clickable
                nextBtn.removeAttribute('disabled');

                const nextHandler = (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (this.swiper) {
                        this.swiper.slideNext();
                    }
                };
                nextBtn.addEventListener('click', nextHandler);
            }

            if (prevBtn) {
                // Remove disabled attribute to ensure button is clickable
                prevBtn.removeAttribute('disabled');

                const swiperInstance = this.swiper;
                const prevHandler = (e) => {
                    if (swiperInstance) {
                        swiperInstance.slidePrev();
                    }
                    return false;
                };

                // Attach event handlers
                prevBtn.onclick = prevHandler;
                prevBtn.addEventListener('click', prevHandler, true);
                prevBtn.addEventListener('mousedown', prevHandler, true);
            }
        }

        // Slide change event dispatch
        this.swiper.on('slideChange', () => {
            this.dispatchEvent(
                new CustomEvent('slideChange', {
                    detail: { swiper: this.swiper },
                }),
            );
            const nextBtn = this.querySelector('.swiper-button-next');
            const prevBtn = this.querySelector('.swiper-button-prev');
            nextBtn?.classList.remove('swiper-button-disabled');
            prevBtn?.classList.remove('swiper-button-disabled');
        });
    };

    getOptions = () => {
        let options = {};

        for (let key in this.defaultOptions) {
            let value = this.getAttribute(this.camelCaseToKebabCase(key));

            if (value !== null) {
                if (key === 'navigation' && value === 'true') {
                    let nextEl = null;
                    let prevEl = null;

                    // First, try to find using section ID (most reliable)
                    const containerId = this.getAttribute('id');
                    if (containerId) {
                        // Extract section ID from container ID (e.g., "swiper-container-12345" -> "12345")
                        const sectionId = containerId.replace('swiper-container-', '');
                        nextEl = document.getElementById(`slider-button--next-${sectionId}`);
                        prevEl = document.getElementById(`slider-button--prev-${sectionId}`);
                    }

                    // If not found by ID, try to find within this swiper container
                    if (!nextEl) {
                        nextEl = this.querySelector('.swiper-button-next');
                    }
                    if (!prevEl) {
                        prevEl = this.querySelector('.swiper-button-prev');
                    }

                    // Last resort: look in parent element
                    if (!nextEl && this.parentElement) {
                        nextEl = this.parentElement.querySelector('.swiper-button-next');
                    }
                    if (!prevEl && this.parentElement) {
                        prevEl = this.parentElement.querySelector('.swiper-button-prev');
                    }

                    // Don't pass navigation to Swiper - we'll handle it manually
                    // This prevents Swiper from disabling buttons it can't find
                    // Mark that we found navigation buttons so we can handle them manually
                    options._manualNavigation = nextEl || prevEl ? { nextEl, prevEl } : null;

                    // Don't set options[key] - skip Swiper's built-in navigation entirely
                } else if (key === 'pagination' && value === 'true') {
                    // Use scoped pagination element within this swiper container
                    const paginationEl = this.querySelector('.swiper-pagination');
                    options[key] = {
                        el: paginationEl,
                        clickable: true,
                    };
                } else if (key === 'thumbs') {
                    const target = document.querySelector(value)?.querySelector('.swiper');
                    if (target) {
                        options[key] = { swiper: target };
                    }
                } else if (key === 'delay' && options['autoplay']) {
                    options['autoplay'] = { delay: parseInt(value) };
                } else if (key === 'breakpoints') {
                    let breakpoints = {};
                    this.breakpoints = Array.from(this.querySelectorAll('breakpoint')).map((breakpoint) => {
                        let subOptions = {};
                        for (let subKey in this.defaultOptions) {
                            let subValue = breakpoint.getAttribute(this.camelCaseToKebabCase(subKey));
                            if (subValue !== null) {
                                if (typeof this.defaultOptions[subKey] === 'boolean') {
                                    subOptions[subKey] = !(String(subValue).toLowerCase() === 'false');
                                } else if (typeof this.defaultOptions[subKey] === 'number') {
                                    subOptions[subKey] = parseFloat(subValue);
                                } else {
                                    subOptions[subKey] = subValue;
                                }
                            }
                        }
                        let size = breakpoint.getAttribute('size');
                        breakpoints[size] = subOptions;
                    });
                    options[key] = breakpoints;
                } else if (key === 'creativeEffect') {
                    try {
                        options[key] = JSON.parse(value);
                    } catch (e) {}
                } else if (typeof this.defaultOptions[key] === 'boolean') {
                    options[key] = !(String(value).toLowerCase() === 'false');
                } else if (typeof this.defaultOptions[key] === 'number') {
                    options[key] = parseFloat(value);
                } else {
                    options[key] = value;
                }
            }
        }

        return options;
    };

    slideTo = (n) => {
        this.swiper?.slideTo(n);
    };

    slideToLoop = (n) => {
        this.swiper?.slideToLoop(n);
    };

    camelCaseToKebabCase = (str) =>
        str
            .split('')
            .map((letter, index) =>
                letter.toUpperCase() === letter ? `${index === 0 ? '' : '-'}${letter.toLowerCase()}` : letter,
            )
            .join('');
}

customElements.define('swiper-container', SwiperContainer);
